'use client'

import type {
  Questionnaire,
  QuestionnairePage as QuestionnaireStepPage,
} from '@/lib/questionnaires/Questionnaire'
import { Form, FormikProps, getIn, useFormikContext } from 'formik'
import ProgressBar from '@/shared/components/questionnaires/ProgressBar'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import QuestionnairePage from '@/shared/components/questionnaires/QuestionnairePage'
import FormButtonGroup from '@/shared/components/questionnaires/FormButtonGroup'
import { PatientProfileValidator } from '@/lib/apis/validators'
import QuestionnaireReview from '@/shared/components/questionnaires/QuestionnaireReview'
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion'
import { UploadDocumentContainersProps } from '@/shared/components/UploadDocumentContainer'
import { SupportingDocument } from '@/features/prescriptions/SupportingDocumentItem'
import { PatientProfile, ProfileStatus } from '@/lib/apis/types'
import { useTfaStore } from '@/features/tfa/store'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { useMutation } from '@tanstack/react-query'
import { StartTfaChallenge, VerifyTfaChallenge } from '@/lib/apis/tfa'
import CallArrangementForm, {
  CallArrangementFormProps,
} from '@/features/common-requests/call-arrangement-form'

type ProfileLikeForm = {
  mobile: string
}

type Props<T extends ProfileLikeForm> = {
  profileStatus: ProfileStatus | null
  initialProfile: PatientProfile | null
  authenticated?: boolean
  onSaveAuthenticatedProfile?: (
    values: T,
    mobileVerificationToken?: string,
  ) => Promise<void>
  questionnaire: Questionnaire
  formikProps: FormikProps<T>
  uploadConfig?: UploadDocumentContainersProps
  documents: Array<SupportingDocument>
  disclaimer?: ReactNode
  callArrangementFormProps?: CallArrangementFormProps
}

type MobileVerificationStatus = {
  address: string
  verified: boolean
}

export default function QuestionnairePageContainer<T extends ProfileLikeForm>({
  profileStatus,
  initialProfile,
  authenticated = false,
  onSaveAuthenticatedProfile,
  questionnaire,
  formikProps,
  uploadConfig,
  documents,
  disclaimer,
  callArrangementFormProps,
}: Props<T>) {
  const [title, setTitle] = useState('Digital Consultation')
  const [[page, direction], setPage] = useState([0, 0])
  const { values, isSubmitting, validateForm, setFieldTouched, setFieldValue } =
    useFormikContext<T>()
  const advancing = useRef(false)
  const [isAdvancing, setIsAdvancing] = useState(false)

  const [mobileVerificationStatus, setMobileVerificationStatus] =
    useState<MobileVerificationStatus>({
      address: initialProfile?.mobile ?? '',
      verified: profileStatus?.mobileVerified ?? false,
    })

  const startTfaVerification = useTfaStore(
    (state) => state.startTfaVerification,
  )

  const api = useApiClient()
  const startTfaChallengeMutation = useMutation(StartTfaChallenge.mutation(api))
  const verifyTfaChallengeMutation = useMutation(
    VerifyTfaChallenge.mutation(api),
  )

  const animation: Pick<
    HTMLMotionProps<'div'>,
    'initial' | 'animate' | 'transition'
  > = {
    initial: { x: direction < 0 ? '-3%' : '3%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { ease: 'easeInOut', duration: 0.3 },
  }

  let nextButtonDisabled = false
  let questionnairePage
  let requiresMobileVerification = false
  const currentPage =
    page < questionnaire.pages.length ? questionnaire.pages[page] : null

  if (currentPage) {
    if (
      currentPage === 'personal-information' &&
      values.mobile &&
      (values.mobile !== mobileVerificationStatus.address ||
        !mobileVerificationStatus.verified)
    ) {
      requiresMobileVerification = true
    }

    if (currentPage === 'upload-supporting-document') {
      if (uploadConfig === undefined)
        throw new Error('Missing upload config with upload form')

      questionnairePage = (
        <QuestionnairePage
          {...animation}
          page={currentPage}
          formikProps={formikProps}
          uploadConfig={uploadConfig}
        />
      )
    } else if (currentPage === 'call-arrangement') {
      if (!callArrangementFormProps)
        throw new Error('Missing call arrangement form props')

      questionnairePage = <CallArrangementForm {...callArrangementFormProps} />
      nextButtonDisabled = callArrangementFormProps.selectedSchedule === null
    } else {
      questionnairePage = (
        <QuestionnairePage
          {...animation}
          page={currentPage}
          formikProps={formikProps}
          disabledProfileFields={authenticated ? new Set(['email']) : undefined}
          uploadConfig={undefined}
        />
      )
    }
  } else {
    questionnairePage = (
      <>
        <QuestionnaireReview
          questionnaire={questionnaire}
          values={values}
          documents={documents}
        />
        {renderSchedule()}
      </>
    )
  }

  nextButtonDisabled ||=
    documents.filter((document) => document.uploadState.uploading).length > 0

  // Back button is always shown
  const backButtonDisabled = page === 0 || isSubmitting || isAdvancing

  // Next button label should says preview if it's the last page
  const nextButtonLabel =
    page === questionnaire.pages.length - 1 && !requiresMobileVerification
      ? 'Review'
      : 'Next'

  // Show submit button if all the steps are completed
  const showSubmit = page === questionnaire.pages.length

  let nextButtonState: 'hidden' | 'disabled' | 'enabled' | 'loading' = 'hidden'
  if (!showSubmit) {
    /*
     * The next button is
     *   - hidden when submit button is shown
     *   - disabled when
     *       * a call time is still required
     *       * the documents are uploading
     *   - loading while validating, verifying or saving the current step
     *   - enabled otherwise
     * */
    if (nextButtonDisabled || isSubmitting) {
      nextButtonState = 'disabled'
    } else if (isAdvancing || startTfaChallengeMutation.isPending) {
      nextButtonState = 'loading'
    } else {
      nextButtonState = 'enabled'
    }
  }
  function renderSchedule() {
    if (!callArrangementFormProps || !callArrangementFormProps.selectedSchedule)
      return null

    switch (callArrangementFormProps.selectedSchedule.type) {
      case 'immediate':
        return (
          <div className="mt-4 flex flex-col gap-y-2">
            <h2 className="text-lg font-semibold tracking-tight text-gray-800">
              Appointment Schedule
            </h2>
            <p className="text-sm leading-6 text-gray-700">
              One of our doctors will call you within 2 hours.
            </p>
          </div>
        )
      case 'scheduled':
        const { day, date, time } =
          callArrangementFormProps.selectedSchedule.selected

        return (
          <div className="mt-4 flex flex-col gap-y-2">
            <h2 className="text-lg font-semibold tracking-tight text-gray-800">
              Appointment Schedule
            </h2>
            <p className="text-sm leading-6 text-gray-700">
              One of our doctors will call you within 2 hours of your
              appointment time:{' '}
              <span className="font-medium text-gray-800">
                {date} ({day}) at {time}
              </span>
              .
            </p>
          </div>
        )
    }
  }

  function changePageTitle(page: QuestionnaireStepPage, lastPage: boolean) {
    if (lastPage) {
      setTitle('Review Your Request')
    } else if (page === 'personal-information') {
      setTitle('Add Your Details')
    } else if (page === 'upload-supporting-document') {
      setTitle('Upload Supporting Documents')
    } else if (page === 'call-arrangement') {
      setTitle('When do you want to be called?')
    } else {
      setTitle('Digital Consultation')
    }
  }

  function onChangePage(newDirection: number) {
    const newPage = page + newDirection
    setPage([newPage, newDirection])
    window.scrollTo(0, 0)

    const target = questionnaire.pages[newPage]
    changePageTitle(target, newPage === questionnaire.pages.length)
  }

  useEffect(() => {
    changePageTitle(
      questionnaire.pages[page],
      page === questionnaire.pages.length,
    )
  })

  return (
    <>
      <div className="mx-0 mt-10 w-full max-w-4xl space-y-12 bg-white px-2 py-4 sm:mx-auto sm:mt-32 sm:rounded-lg sm:border sm:border-gray-200 sm:px-10 sm:py-8 sm:shadow-sm">
        <div className="flex flex-col gap-y-2">
          <h1 className="empty:before:content-[' '] text-2xl font-bold tracking-tight text-gray-900 empty:before:inline-block">
            {title}
          </h1>
          {page === 0 && disclaimer}
        </div>
        <Form noValidate>
          <AnimatePresence mode="popLayout">
            <motion.div key={page} {...animation}>
              {questionnairePage}
            </motion.div>
          </AnimatePresence>
          <FormButtonGroup
            backButtonState={backButtonDisabled ? 'disabled' : 'enabled'}
            nextButtonLabel={nextButtonLabel}
            nextButtonState={nextButtonState}
            submitButtonState={showSubmit ? 'enabled' : 'hidden'}
            onBackButtonClick={() => onChangePage(-1)}
            onNextButtonClick={async () => {
              if (
                advancing.current ||
                isSubmitting ||
                nextButtonDisabled ||
                !currentPage
              )
                return
              advancing.current = true
              setIsAdvancing(true)
              try {
                const fieldIds = extractFieldIds(currentPage)
                const validatedErrors = await validateForm()
                const invalidFields = fieldIds.filter((id) =>
                  getIn(validatedErrors, id),
                )
                if (invalidFields.length) {
                  await Promise.all(
                    invalidFields.map((id) => setFieldTouched(id, true, false)),
                  )
                  return
                }
                if (requiresMobileVerification) {
                  await startTfaVerification([
                    {
                      channel: 'Sms',
                      address: values.mobile,
                      verify: async (token, code) => {
                        const data =
                          await verifyTfaChallengeMutation.mutateAsync({
                            data: {
                              token,
                              code,
                              channel: 'Sms',
                              address: values.mobile,
                              intent: authenticated
                                ? 'PROFILE_UPDATE'
                                : 'ACCOUNT_CREATION',
                            },
                          })
                        return data.token
                      },
                      onSuccessfulVerification: async (token) => {
                        await onSaveAuthenticatedProfile?.(values, token)
                        if (!authenticated) {
                          await setFieldValue('mobileVerificationToken', token)
                        }
                        setMobileVerificationStatus({
                          verified: true,
                          address: values.mobile,
                        })

                        onChangePage(+1)
                      },
                      resendCode: async () => {
                        const data =
                          await startTfaChallengeMutation.mutateAsync({
                            data: {
                              address: values.mobile,
                              channel: 'Sms',
                              intent: authenticated
                                ? 'PROFILE_UPDATE'
                                : 'ACCOUNT_CREATION',
                            },
                          })
                        return data.token
                      },
                      tokenState: {},
                    },
                  ])

                  return
                }

                if (currentPage === 'personal-information') {
                  await onSaveAuthenticatedProfile?.(values)
                }
                onChangePage(+1)
              } finally {
                advancing.current = false
                setIsAdvancing(false)
              }
            }}
          />
        </Form>
      </div>

      {/* On preview page, progress bar should be hidden */}
      {page < questionnaire.pages.length && (
        <div className="flex w-full justify-center py-6">
          <ProgressBar steps={questionnaire.pages.length} current={page} />
        </div>
      )}
    </>
  )
}

export function extractFieldIds(page: QuestionnaireStepPage): string[] {
  if (page === 'personal-information') {
    return Object.keys(PatientProfileValidator.fields)
  } else if (
    page === 'upload-supporting-document' ||
    page === 'call-arrangement'
  ) {
    return []
  }

  return page.questions.flatMap((question) => {
    if (question.type === 'date-range') {
      return [question.startKey, question.endKey]
    } else if (question.type === 'duration') {
      return [question.yearKey, question.monthKey]
    } else {
      return [question.key]
    }
  })
}
